import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components';
import {
  useAppSettingsStore,
  useLanguageOptions,
  useMediaQuery,
} from '@/shared/lib';
import { cn } from '@/shared/utils';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  tooltipOpen: boolean;
  setTooltipOpen: () => void;
  children: JSX.Element;
}

export const UserLanguageTooltip = ({
  tooltipOpen,
  setTooltipOpen,
  children,
}: Props) => {
  const { t } = useTranslation();

  const { setLanguage } = useAppSettingsStore();

  const { languageOptions } = useLanguageOptions();
  const isMobile = useMediaQuery('(max-width: 1024px)');

  return (
    <Tooltip open={tooltipOpen}>
      <TooltipTrigger asChild onClick={() => setTooltipOpen()}>
        {children}
      </TooltipTrigger>
      <TooltipContent
        side={isMobile ? 'bottom' : 'right'}
        sideOffset={isMobile ? 0 : 14}
        style={isMobile ? undefined : { transform: 'translateY(28px)' }}
        className={cn(
          'flex items-center flex-col p-[0.8rem] rounded-[1.6rem] h-[12rem] gap-[0.8rem]',
          isMobile ? 'w-[32.2rem] max-w-[32.2rem]' : 'w-[24rem] min-w-[24rem]'
        )}>
        {languageOptions.map(({ value, code, icon }) => (
          <div
            key={value}
            onClick={() => {
              setTooltipOpen();
              setLanguage(value);
            }}
            className="flex items-center hover:bg-accent hover:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative cursor-default py-[1.2rem] pr-[0.8rem] pl-[0.8rem] text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 w-full max-w-full gap-[0.8rem] min-h-[4.8rem] h-[4.8rem] w-full rounded-[1.2rem] cursor-pointer">
            <div className="w-[3.2rem] h-[3.2rem] flex items-center justify-center">
              {icon != '' && (
                <img src={icon} className="size-[3.2rem] text-primary" />
              )}
            </div>

            <span className="headline4 text-primary flex-1">
              {t(`common.language.options.${code}`)}
            </span>
          </div>
        ))}
      </TooltipContent>
    </Tooltip>
  );
};
